/* eslint no-param-reassign: "error" */
import React, {
  useCallback,
  useEffect,
} from 'react';
import {
  DateTime,
} from 'luxon';
import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';
import i18n from 'i18next';
import {
  getStatus,
} from 'redux-resource';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  Icon,
  Spinner,
} from 'web-components';
import {
  Flex,
  Text,
} from 'web-styled';

import * as S from './styled';
import ScheduleWorkout from '../CalendarTab/ScheduleWorkout';

type Props = {
  clientId: number,
  tabName: string,
};

const RightWidget = ({
  tabName,
  clientId,
}: Props) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      coreActions.requestNotifications(clientId),
    );

    const range = {
      range_from: DateTime.utc().startOf('month'),
      range_to: DateTime.utc().endOf('month'),
    };
    dispatch(
      coreActions.requestLoadWorkoutEvents({
        params: {
          ...range,
          scope: 'all',
        },
        userId: clientId,
      }),
    );
    dispatch(
      coreActions.requestClientPersonalEntity({
        clientId,
        qs: range,
        resourceType: 'activityLogs',
      }),
    );
  }, [clientId]);

  const mapState = useCallback(state => ({
    readStatus: getStatus(
      state,
      [
        'notifications.requests.fetchNotifications.status',
      ],
      true,
    ),
    readStatusEvent: getStatus(
      state,
      [
        'workoutEvents.requests.fetchWorkoutEvents.status',
        'activityLogs.requests.fetchActivityLogs.status',
      ],
      true,
    ),
    clientActivity: coreSelectors.getClientRecentActivity(clientId)(state),
    workoutsOptions: coreSelectors.getSearchClientOptions('personalWorkouts', String(clientId))(state),
    currentCalendarDate: getUiState('currentCalendarDate')(state),
  }), [clientId]);

  const {
    readStatus,
    readStatusEvent,
    clientActivity,
    workoutsOptions,
    currentCalendarDate,
  } = useSelector(mapState, shallowEqual);

  const onScheduleWorkoutCancel = () => {
    dispatch(
      uiActions.setUiState({ currentCalendarDate: null }),
    );
  };

  const onScheduleWorkoutSubmit = (values) => {
    dispatch(
      coreActions.requestCreateWorkoutEvents({
        userId: clientId,
        payload: {
          begins_at: values.start,
          ends_at: values.end,
          personal_workout_id: +values.workout,
          is_valid: true,
        },
      }),
    );
    dispatch(
      uiActions.setUiState({
        currentCalendarDate: null,
      }),
    );
  };

  const showScheduleWidget = tabName === 'calendar' && currentCalendarDate;
  const isLoading = readStatus.pending || readStatusEvent.pending;

  return (
    <S.Activity>
      <S.ActivityHeader>
        <Text
          size="18px"
          lineHeight="54px"
          color="#cbcacb"
          transform="uppercase"
          weight={500}
        >
          {showScheduleWidget ? i18n.t('schedule.new.workout') : 'Recent Activity'}
        </Text>
      </S.ActivityHeader>
      {showScheduleWidget
        ? (
          <Flex
            fillSpace
            padding="20px"
          >
            <ScheduleWorkout
              onSubmit={onScheduleWorkoutSubmit}
              onCancel={onScheduleWorkoutCancel}
              startDate={currentCalendarDate}
              clientId={clientId}
              workoutsOptions={workoutsOptions}
            />
          </Flex>
        ) : (
          <Flex fillSpace>
            {isLoading ? (
              <Spinner message="Loading Activity..." />
            ) : (
              <Flex fillSpace>
                {clientActivity.length ? (
                  <Flex
                    width="100%"
                    height="100%"
                    column
                  >
                    {clientActivity.map(item => (
                      <Flex
                        width="100%"
                        margin="10px"
                        key={item.key}
                      >
                        <Flex
                          width="10%"
                          justify="center"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            width={12}
                            height={12}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 6v6l4 2" />
                            <circle cx="12" cy="12" r="10" />
                          </svg>
                        </Flex>
                        <Flex
                          width="calc(80% - 15px)"
                          column
                        >
                          <Text
                            size="12px"
                            margin="0 0 10px"
                          >
                            {item.time}
                          </Text>
                          <Text
                            margin="0 0 10px"
                          >
                            {item.activityText}
                          </Text>
                        </Flex>
                      </Flex>
                    ))}
                  </Flex>
                ) : (
                  <Flex width="100%" spacing={10} column align="center" margin="30px 0 0 0">
                    <S.PlaneIcon />
                    <Text
                      size="13px"
                      align="center"
                      lineHeight="16px"
                      color="#cbcacb"
                      weight={500}
                    >
                      Soon you will see
                      <br />
                      activity updates here
                    </Text>
                  </Flex>
                )}
              </Flex>
            )}
          </Flex>
        )}
    </S.Activity>
  );
};

export default RightWidget;
